import React from "react";
import { ModuleConfig } from "../../types/ModuleConfig";

// Import master site editor components (lazy-loaded for performance)
// These are for SITE_EDITOR_MASTER_ADMIN managing all sites across tenants
const SitesList = React.lazy(() => import("./SitesList"));
const SiteEditor = React.lazy(() => import("./SiteEditor"));
const NewSite = React.lazy(() => import("./NewSite"));
const EditSite = React.lazy(() => import("./EditSite"));
const CommandHistory = React.lazy(() => import("./CommandHistory"));

/**
 * Master Site Editor Module Config
 *
 * SINGLE SOURCE OF TRUTH:
 * Permissions are defined ONCE in the backend (siteeditor/permissions.ts)
 * and fetched via the modulePermissions GraphQL query.
 *
 * Routes and nav items use `permissionLevel` (e.g. "view", "create", "edit")
 * which gets resolved to actual permission strings at runtime from the backend.
 *
 * To change permissions: Edit business-builder-backend/src/modules/siteeditor/permissions.ts
 * That's the only place. The frontend picks it up automatically.
 */
const masterSiteEditorModuleConfig: ModuleConfig = {
    // MODULE IDENTITY
    id: "master-site-editor",
    name: "Master Site Editor",
    description: "Manage and edit all tenant websites with Claude AI",
    version: "1.0.0",
    enabled: true,
    category: "development",
    order: 51,
    icon: "🔧",
    color: "#7C3AED", // Purple for master admin

    // ROUTES - permissionLevel is resolved from backend at runtime
    routes: [
        {
            path: "/master-site-editor",
            component: SitesList,
            permissionLevel: "view",
        },
        {
            path: "/master-site-editor/new",
            component: NewSite,
            permissionLevel: "create",
        },
        {
            path: "/master-site-editor/:siteId",
            component: SiteEditor,
            permissionLevel: "view",
        },
        {
            path: "/master-site-editor/:siteId/settings",
            component: EditSite,
            permissionLevel: "edit",
        },
        {
            path: "/master-site-editor/:siteId/history",
            component: CommandHistory,
            permissionLevel: "view",
        }
    ],

    // NAVIGATION - permissionLevel is resolved from backend at runtime
    navigation: [
        {
            label: "All Sites",
            path: "/master-site-editor",
            icon: "🌐",
            permissionLevel: "view",
        },
        {
            label: "Add Site",
            path: "/master-site-editor/new",
            icon: "➕",
            permissionLevel: "create",
        }
    ],

    // Permissions come from the backend (single source of truth).
    // See: business-builder-backend/src/modules/siteeditor/permissions.ts
    // These empty arrays get replaced at runtime by ModulePermissionsContext.
    permissions: {
        view: [],
        create: [],
        edit: [],
        delete: [],
    },

    // QUICK ACTIONS
    quickActions: [
        {
            label: "Manage Sites",
            path: "/master-site-editor",
            icon: "🌐",
            description: "View and manage all tenant websites"
        },
        {
            label: "Add New Site",
            path: "/master-site-editor/new",
            icon: "➕",
            description: "Configure a new site for editing"
        }
    ],

    // CHANGELOG
    changelog: [
        {
            version: "1.0.0",
            releaseDate: "2025-12-12",
            title: "Initial Release - Split from Site Editor",
            features: [
                "Manage all tenant websites from one dashboard",
                "Session-based editing with automatic PR creation",
                "Real-time dev server control (start/stop)",
                "Claude relay integration for AI edits",
                "Command history and audit trail",
                "Site configuration management"
            ],
            notes: "Master Site Editor separated from tenant Site Editor for cleaner UX"
        }
    ],

    // ROADMAP
    roadmap: []
};

export default masterSiteEditorModuleConfig;
